// --- React & Redux
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
// --- External UI Libraries
import { Col, Row } from "antd";
import Button from "@dtci/spellbook/Button";
// --- Components
import { JobTasksGrid } from "./grid";
import { DetailsModal } from "tap/components/DetailsModal/index.jsx";
import { PaginationBar } from "tap/components/PaginationBar/index.jsx";
// --- Hooks & Actions
import { useJobsData } from "../../../hooks/useJobsData";
import { useJobTasksData } from "../../../hooks/useJobTasksData";
import { useRecursiveTimeout } from "../../../../LicensingJobs/hooks/useRecursiveTimeout";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { useStoredModalItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- Utils
import { getItemRange } from "tap/utils/pagination.js";
// --- Styles
import "./styles.scss";

// Auto-refresh interval in milliseconds (15 seconds)
const AUTO_REFRESH_INTERVAL = 15000;

/**
 * Job Tasks Modal
 *
 * Features:
 * - Displays a modal with job tasks in a grid
 * - Supports pagination to navigate through tasks
 * - Fetches task data from API based on filters and sorting
 *
 * @returns {JSX.Element}
 */
export const JobTasksModal = () => {
  const {
    isTasksModalOpen,
    jobTasksTotalPages,
    jobTasksCurrentPage,
    jobTasksTotalItems,
    selectedJob,
  } = useSelector((state) => state.tapJobs);

  const { fetchJobs, updateJobs: updateJobsData, tapJobRange } = useJobsData();
  const { fetchJobTasks, updateJobs, jobTasksItemsPerPage } = useJobTasksData();
  const { clearJobTasksModal } = useJobsActions();

  const applyJobTasksData = useCallback(
    (data) => {
      if (!data) return;
      updateJobs({
        jobTasks: data?.content,
        jobTasksTotalPages: data?.totalPages,
        jobTasksTotalItems: data?.totalElements,
        jobTasksCurrentPage: data?.pageable?.pageNumber,
      });
    },
    [updateJobs],
  );

  const fetchTasksPage = useCallback(
    async (pageNumber, pageSize = jobTasksItemsPerPage, silent = false) => {
      const range = getItemRange(pageNumber, pageSize);
      const data = await fetchJobTasks(range, null, null, silent);
      applyJobTasksData(data);
      return data;
    },
    [applyJobTasksData, fetchJobTasks, jobTasksItemsPerPage],
  );

  useStoredModalItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.tapJobsTasks,
    isOpen: isTasksModalOpen,
    itemsPerPage: jobTasksItemsPerPage,
    onSync: updateJobs,
    itemsPerPageField: "jobTasksItemsPerPage",
    currentPageField: "jobTasksCurrentPage",
  });

  // Close modal and refresh the jobs list
  const onCloseModal = async () => {
    clearJobTasksModal();
    const data = await fetchJobs(tapJobRange, null, null, true);
    if (data) {
      updateJobsData({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  // Handle page change in pagination
  const handlePageChange = async (pageNumber) => {
    await fetchTasksPage(pageNumber);
  };

  const handlePageSizeChange = async (newSize) => {
    const data = await fetchTasksPage(0, newSize);
    if (data) {
      updateJobs({ jobTasksItemsPerPage: newSize });
    }
  };

  const handleRefresh = () => {
    fetchTasksPage(jobTasksCurrentPage);
  };

  // Auto-refresh tasks while the modal is open
  useRecursiveTimeout(
    () => fetchTasksPage(jobTasksCurrentPage, jobTasksItemsPerPage, true),
    AUTO_REFRESH_INTERVAL,
    isTasksModalOpen,
  );

  return (
    <DetailsModal
      modalHeader={
        <Row className="job-tasks-modal-header">
          <Col className="job-tasks-modal-title">
            <span>Tasks for job: {selectedJob?.id}</span>
          </Col>
          <Col className="job-tasks-modal-actions">
            <Button
              iconType="fa-solid fa-rotate"
              labelText="Refresh"
              styleType="link"
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      }
      isOpen={isTasksModalOpen}
      onClose={onCloseModal}
      footer={
        <div className="job-tasks-modal-footer">
          <PaginationBar
            totalPages={jobTasksTotalPages}
            currentPage={jobTasksCurrentPage}
            totalItems={jobTasksTotalItems}
            itemsPerPage={jobTasksItemsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      }
    >
      <JobTasksGrid />
    </DetailsModal>
  );
};
